/**
 * Shared focus mode constants for consistent usage across the application
 * These represent strategic approaches to segments, propositions, and positions
 */

export interface FocusMode {
  value: string;
  label: string;
  color: "error" | "warning" | "info" | "success" | "default" | "secondary";
  description: string;
}

export const FOCUS_MODES: FocusMode[] = [
  {
    value: "focus",
    label: "Focus",
    color: "error",
    description: "High priority - immediate attention and resources",
  },
  {
    value: "explore",
    label: "Explore",
    color: "warning",
    description: "Medium priority - investigate and develop opportunities",
  },
  {
    value: "opportunistic",
    label: "Opportunistic",
    color: "info",
    description: "Low priority - pursue if resources available",
  },
  {
    value: "maintain",
    label: "Maintain",
    color: "success",
    description: "Stable priority - keep current position",
  },
  {
    value: "exit",
    label: "Exit",
    color: "secondary",
    description: "Plan to discontinue or withdraw",
  },
  {
    value: "none",
    label: "None",
    color: "default",
    description: "No specific focus applied",
  },
];

// Special focus mode for invalid combinations
export const NOT_RELEVANT_FOCUS_MODE = {
  value: "not_relevant",
  label: "Not Relevant",
  color: "default" as const,
  description: "Invalid segment/proposition combination",
};

// Helper function to get focus mode by value
export function getFocusMode(
  value: string | null | undefined,
): FocusMode | null {
  if (!value) return null;

  // Normalize the value for comparison (handle both lowercase and Title Case)
  const normalizedValue = value.toLowerCase();
  
  // Check regular focus modes with case-insensitive comparison
  const mode = FOCUS_MODES.find((fm) => fm.value.toLowerCase() === normalizedValue);
  if (mode) return mode;

  // Check for Not Relevant (both formats)
  if (normalizedValue === 'not relevant' || normalizedValue === 'not_relevant') {
    return NOT_RELEVANT_FOCUS_MODE;
  }

  return null;
}

// Helper to get all focus modes including Not Relevant (for display purposes)
export function getAllFocusModes(): FocusMode[] {
  return [...FOCUS_MODES, NOT_RELEVANT_FOCUS_MODE];
}

// Helper to get focus modes for selection (excluding Not Relevant)
export function getSelectableFocusModes(): FocusMode[] {
  return FOCUS_MODES;
}

// Color mapping for backward compatibility
export function getFocusModeColor(
  value: string | null | undefined,
): "error" | "warning" | "info" | "success" | "default" | "secondary" {
  const mode = getFocusMode(value);
  return mode?.color || "default";
}

// Helper to get Tailwind classes for focus modes
// This matches the same color scheme used across the application
// Optional: include borders for chips that need them
export function getFocusModeTailwindClasses(
  value: string | null | undefined,
  includeBorder = false,
): string {
  // Normalize to lowercase for consistent comparison
  const normalizedValue = value?.toLowerCase();
  
  switch (normalizedValue) {
    case "focus":
      return `bg-green-100 text-green-800${includeBorder ? " border-green-300" : ""}`;
    case "explore":
      return `bg-yellow-100 text-yellow-800${includeBorder ? " border-yellow-300" : ""}`;
    case "opportunistic":
      return `bg-blue-100 text-blue-800${includeBorder ? " border-blue-300" : ""}`;
    case "maintain":
      return `bg-gray-100 text-gray-800${includeBorder ? " border-gray-300" : ""}`;
    case "exit":
      return `bg-red-100 text-red-800${includeBorder ? " border-red-300" : ""}`;
    case "none":
      return `bg-gray-100 text-gray-600${includeBorder ? " border-gray-300" : ""}`;
    case "not relevant":
    case "not_relevant":
      return `bg-gray-100 text-gray-500${includeBorder ? " border-gray-300" : ""}`;
    default:
      return `bg-gray-100 text-gray-600${includeBorder ? " border-gray-300" : ""}`;
  }
}
